import React from 'react';
import { Row, Col, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Label, Input, Spinner, Button } from 'reactstrap';
import classnames from 'classnames';
import { useCatalogItems } from '../hooks/useCatalogItems';
import { useSalesSelection } from '../hooks/useSalesSelection';
import PaymentForm from './PaymentForm';
import { formatCurrency } from '../../../../utils/format';

const SalesSelectionPanel = ({
    activeTab,
    toggleTab,
    onAddPayment,
    onAddItem,
    memberships = [],
    acquirers = [],
    suggestedValue = 0,
    saleDate,
    setSaleDate,
    startDate,
    setStartDate,
    discount,
    setDiscount,
    cartItems = [],
    pendingEnrollments = {},
    onOpenEnrollment = null,
    onClearEnrollment = null
}) => {
    // Dados reais do catálogo (substitui MOCK_PRODUCTS / MOCK_SERVICES)
    const { products, services, loadingCatalog } = useCatalogItems();

    const { handleSelectionChange } = useSalesSelection({ onAddItem });

    return (
        <Card className="shadow-sm border-0">
            <CardBody className="d-flex flex-column">

                {/* NAV TABS */}
                <Nav tabs className="nav-tabs-custom mb-4">
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab === '1' })} onClick={() => toggleTab('1')} style={{ cursor: 'pointer' }}>
                            <span className="fw-bold">PLANO</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab === '2' })} onClick={() => toggleTab('2')} style={{ cursor: 'pointer' }}>
                            <span className="fw-bold">PRODUTOS</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab === '3' })} onClick={() => toggleTab('3')} style={{ cursor: 'pointer' }}>
                            <span className="fw-bold">SERVIÇOS</span>
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* CAMPOS GLOBAIS DA VENDA (sempre visíveis) */}
                <div className="bg-light p-3 rounded mb-4 border border-light-subtle shadow-sm">
                    <Row className="g-3">
                        <Col md={3}>
                            <Label className="form-label fw-bold text-dark font-size-12 mb-1">Data da Venda</Label>
                            <Input
                                type="date"
                                className="form-control form-control-sm border-0"
                                value={saleDate || ''}
                                onChange={(e) => setSaleDate(e.target.value)}
                            />
                            <small className="text-muted d-block mt-1" style={{ fontSize: '9px' }}>Data retroativa para histórico</small>
                        </Col>
                        <Col md={3}>
                            <Label className="form-label fw-bold text-dark font-size-12 mb-1">Data de Início *</Label>
                            <Input
                                type="date"
                                className="form-control form-control-sm border-0"
                                value={startDate || ''}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <small className="text-muted d-block mt-1" style={{ fontSize: '9px' }}>Início da vigência</small>
                        </Col>
                        <Col md={3}>
                            <Label className="form-label fw-bold text-dark font-size-12 mb-1">Desconto (R$)</Label>
                            <Input
                                type="number"
                                className="form-control form-control-sm border-0"
                                placeholder="0,00"
                                value={discount || ''}
                                onChange={(e) => setDiscount(e.target.value)}
                            />
                        </Col>
                    </Row>
                </div>

                {/* CONTEÚDO DAS TABS */}
                <TabContent activeTab={activeTab} className="flex-grow-1">

                    {/* ABA: PLANO */}
                    <TabPane tabId="1">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold text-dark">Escolha o plano *</Label>
                                <Input
                                    type="select"
                                    className="form-select border-light shadow-sm"
                                    onChange={(e) => handleSelectionChange(memberships, e.target.value, 'membership')}
                                    defaultValue=""
                                >
                                    <option value="" disabled>Selecionar...</option>
                                    {memberships.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.title || c.name} — {c.isScholarship ? 'BOLSISTA' : formatCurrency(parseFloat(c.value || 0))}
                                        </option>
                                    ))}
                                </Input>
                            </Col>
                        </Row>
                    </TabPane>

                    {/* ABA: PRODUTOS (dados reais do Firestore) */}
                    <TabPane tabId="2">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold">Produto</Label>
                                {loadingCatalog ? (
                                    <div className="text-muted small d-flex align-items-center gap-2">
                                        <Spinner size="sm" /> Carregando produtos...
                                    </div>
                                ) : (
                                    <Input
                                        type="select"
                                        className="form-select"
                                        onChange={(e) => handleSelectionChange(products, e.target.value, 'product')}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>
                                            {products.length === 0 ? 'Nenhum produto cadastrado' : 'Buscar produto...'}
                                        </option>
                                        {products.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} — {formatCurrency(parseFloat(p.price))}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            </Col>
                        </Row>
                    </TabPane>

                    {/* ABA: SERVIÇOS (dados reais do Firestore) */}
                    <TabPane tabId="3">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold">Serviço</Label>
                                {loadingCatalog ? (
                                    <div className="text-muted small d-flex align-items-center gap-2">
                                        <Spinner size="sm" /> Carregando serviços...
                                    </div>
                                ) : (
                                    <Input
                                        type="select"
                                        className="form-select"
                                        onChange={(e) => handleSelectionChange(services, e.target.value, 'service')}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>
                                            {services.length === 0 ? 'Nenhum serviço cadastrado' : 'Selecionar...'}
                                        </option>
                                        {services.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name} — {formatCurrency(parseFloat(s.price))}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>

                {/* AREA DE ALERTAS DE MATRÍCULA  - NO CARD PRINCIPAL */}
                {cartItems.filter(item => item.enrollmentRequired).length > 0 && (
                     <div className="mt-4 pt-4 border-top">
                         <h6 className="text-dark fw-bold mb-3 font-size-14">
                             <i className="mdi mdi-account-multiple-plus text-primary me-2"></i> 
                             Turmas Obrigatórias
                         </h6>
                         
                         {cartItems.filter(item => item.enrollmentRequired).map((item) => {
                             const pendings = pendingEnrollments[item.cartId];
                             const hasPendings = pendings && pendings.length > 0;

                             return (
                                <div key={item.cartId} className={`p-3 rounded mb-3 bg-light border ${hasPendings ? 'border-light-subtle' : 'border-warning shadow-sm'}`}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="m-0 fw-bold text-dark font-size-13">{item.name}</h6>
                                        {hasPendings ? (
                                             <span className="badge bg-success-subtle text-success font-size-11"><i className="mdi mdi-check-circle me-1"></i>PRONTAS ({pendings.length})</span>
                                        ) : (
                                             <span className="badge bg-warning-subtle text-warning font-size-11"><i className="mdi mdi-alert-circle me-1"></i>PENDENTE</span>
                                        )}
                                    </div>
                                    <p className="text-muted font-size-12 mb-3">
                                        Exige a seleção prévia das turmas associadas a este plano.
                                    </p>
                                    
                                    {!hasPendings ? (
                                        <Button 
                                            color="primary" 
                                            outline
                                            size="sm"
                                            className="w-100 fw-medium"
                                            onClick={() => onOpenEnrollment && onOpenEnrollment(item.cartId)}
                                        >
                                            <i className="mdi mdi-calendar-plus me-1"></i> Selecionar Turmas
                                        </Button>
                                    ) : (
                                        <div className="d-flex justify-content-between align-items-start bg-white rounded p-2 border border-light-subtle shadow-sm">
                                            <div className="text-muted font-size-12 flex-grow-1 d-flex flex-column gap-2 pe-3">
                                                {pendings.map((enr, i) => (
                                                        <div key={i} className="pb-2 border-bottom border-light last-child-no-border">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <span className="fw-semibold text-dark">
                                                                    {enr.activityName || "Atividade"}
                                                                </span>
                                                                <span className="text-muted font-size-11">
                                                                    <i className="mdi mdi-account-tie me-1"></i>{enr.teacherName || enr.employeeName || "Professor"}
                                                                </span>
                                                            </div>
                                                            <div className="text-muted font-size-11">
                                                                <i className="mdi mdi-calendar-clock me-1"></i>
                                                                <span>{enr.dayOfWeek} às {enr.startTime}</span>
                                                            </div>
                                                        </div>
                                                ))}
                                            </div>
                                            <div className="d-flex flex-column gap-1 shrink-0">
                                                    <Button color="light" size="sm" title="Editar Turmas" onClick={() => onOpenEnrollment && onOpenEnrollment(item.cartId)}>
                                                        <i className="mdi mdi-pencil text-primary"></i>
                                                    </Button>
                                                    <Button color="light" size="sm" title="Limpar Turmas" onClick={() => onClearEnrollment && onClearEnrollment(item.cartId)}>
                                                        <i className="mdi mdi-trash-can-outline text-danger"></i>
                                                    </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                             )
                         })}
                     </div>
                )}
                {/* FORMULÁRIO DE PAGAMENTO (componente extraído) */}
                <PaymentForm
                    acquirers={acquirers}
                    suggestedValue={suggestedValue}
                    onAddPayment={onAddPayment}
                    onAddItem={onAddItem}
                />

            </CardBody>

            <style>{`
                .nav-tabs-custom .nav-link.active { color: var(--bs-primary) !important; border-bottom: 2px solid var(--bs-primary) !important; }
            `}</style>
        </Card>
    );
};

export default SalesSelectionPanel;
